import { useState, useEffect } from "react";
import "./WeeklyReview.css";

const categoryOptions = {
  Work: {
    wins: ["Completed sprint goals", "Improved code quality", "Team collaboration success", "Deadline met", "Feature shipped"],
    improve: ["Testing coverage", "Documentation detail", "Time management", "Communication", "Code review process"],
    skillOptions: ["React", "Node.js", "MongoDB", "Communication", "Leadership", "Problem Solving"]
  },
  Learning: {
    wins: ["Module completed", "Practice finished", "New skill learned", "Concept mastered", "Project built"],
    improve: ["Practice consistency", "Deeper understanding", "Project complexity", "Time management", "Self-discipline"],
    skillOptions: ["JavaScript", "React", "Node.js", "Databases", "System Design", "Problem Solving"]
  },
  SelfDevelopment: {
    wins: ["Streak maintained", "Meditation completed", "Journaling consistency", "Connections made", "Growth achieved"],
    improve: ["Consistency", "Habit formation", "Mindfulness practice", "Reading habit", "Networking"],
    skillOptions: ["Mindfulness", "Communication", "Leadership", "Emotional Intelligence", "Time Management", "Self-discipline"]
  },
  Fitness: {
    wins: ["Workout completed", "Personal record", "Distance achieved", "Consistency maintained", "Progress made"],
    improve: ["Cardio endurance", "Strength gains", "Flexibility", "Recovery time", "Nutrition"],
    skillOptions: ["Cardio", "Strength Training", "Flexibility", "Nutrition", "Recovery", "Discipline"]
  }
};

export default function WeeklyReview() {
  const [review, setReview] = useState({
    category: "Work",
    week: "",
    topic: "",
    wins: "",
    improve: "",
    skills: [],
    focus: "",
    hours: ""
  });
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState("All");

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    fetch("http://localhost:5000/api/weekly", {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setReviews(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReview({ ...review, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setReview({ ...review, category, wins: "", improve: "", skills: [], focus: "" });
  };

  const handleSkillsChange = (e) => {
    const options = Array.from(e.target.selectedOptions).map(o => o.value);
    setReview({ ...review, skills: options });
  };

  const handleSubmit = async () => {
    if (!review.week || !review.topic || !review.wins) {
      alert("Please fill in required fields (Week, Topic, Wins)");
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch("http://localhost:5000/api/weekly", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(review),
      });
      const data = await res.json();
      setReviews([...reviews, data]);
      setReview({ category: "Work", week: "", topic: "", wins: "", improve: "", skills: [], focus: "", hours: "" });
      setShowForm(false);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredReviews = filterCategory === "All" ? reviews : reviews.filter(r => r.category === filterCategory);

  return (
    <div className="weekly-review-container">
      <div className="weekly-header">
        <h2>📊 Weekly Review</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ New Review"}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Create Weekly Review</h3>

          {/* Category Selection */}
          <div className="form-group">
            <label>Category *</label>
            <div className="category-buttons">
              {Object.keys(categoryOptions).map(cat => (
                <button
                  key={cat}
                  className={`category-btn ${review.category === cat ? "active" : ""}`}
                  onClick={() => handleCategoryChange({ target: { value: cat } })}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Week *</label>
              <input type="text" name="week" value={review.week} onChange={handleChange} placeholder="e.g., Week 1, Jan 1-7" />
            </div>
            <div className="form-group">
              <label>Topic *</label>
              <input type="text" name="topic" value={review.topic} onChange={handleChange} placeholder="Main focus area" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Wins This Week *</label>
              <select name="wins" value={review.wins} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryOptions[review.category].wins.map(win => (
                  <option key={win} value={win}>{win}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Areas to Improve</label>
              <select name="improve" value={review.improve} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryOptions[review.category].improve.map(imp => (
                  <option key={imp} value={imp}>{imp}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Skills Practiced (Hold Ctrl/Cmd to select multiple)</label>
            <select multiple value={review.skills} onChange={handleSkillsChange} className="multi-select">
              {categoryOptions[review.category].skillOptions.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </select>
            <div className="skills-display">
              {review.skills.map(skill => (
                <span key={skill} className="skill-tag">{skill}</span>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Focus Next Week</label>
              <input type="text" name="focus" value={review.focus} onChange={handleChange} placeholder="What to focus on next?" />
            </div>
            <div className="form-group">
              <label>Hours Spent</label>
              <input type="number" name="hours" value={review.hours} onChange={handleChange} min="0" max="168" placeholder="0-168" />
            </div>
          </div>

          <button className="btn-submit" onClick={handleSubmit}>Save Review</button>
        </div>
      )}

      <div className="filter-section">
        <label>Filter by Category:</label>
        <div className="filter-buttons">
          {["All", "Work", "Learning", "SelfDevelopment", "Fitness"].map(cat => (
            <button
              key={cat}
              className={`filter-btn ${filterCategory === cat ? "active" : ""}`}
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="reviews-section">
        <h3>Weekly Reviews ({filteredReviews.length})</h3>
        {filteredReviews.length === 0 ? (
          <p className="empty-state">No reviews yet. Create your first weekly review!</p>
        ) : (
          <div className="reviews-grid">
            {filteredReviews.map((r, i) => (
              <div key={i} className={`review-card category-${r.category?.toLowerCase() || "work"}`}>
                <div className="review-header">
                  <span className="badge">{r.category || "Work"}</span>
                  <span className="week-label">{r.week}</span>
                </div>
                <h4>{r.topic}</h4>
                <div className="review-details">
                  {r.wins && <p><strong>✓ Wins:</strong> {r.wins}</p>}
                  {r.improve && <p><strong>→ Improve:</strong> {r.improve}</p>}
                  {r.skills && r.skills.length > 0 && (
                    <div className="skills-list">
                      <strong>Skills:</strong>
                      {r.skills.map((s, idx) => (
                        <span key={idx} className="skill-badge">{s}</span>
                      ))}
                    </div>
                  )}
                  {r.focus && <p><strong>→ Next:</strong> {r.focus}</p>}
                  {r.hours && <p><strong>⏱ Hours:</strong> {r.hours}h</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
       